import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, LogIn, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerLoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          password: password
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Store user data in sessionStorage
        sessionStorage.setItem('customerInfo', JSON.stringify({
          email: data.email,
          name: data.name,
          isSubscription: data.subscription_status === 'active',
          subscription_status: data.subscription_status,
          pickups_used_this_month: data.pickups_used_this_month,
          packages_used_this_month: data.packages_used_this_month
        }));

        // Navigate to customer dashboard
        navigate('/dashboard');
      } else {
        setError(data.detail || 'Invalid email or password');
      }
    } catch (error) {

      setError('Failed to login. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      {/* Header */}
      <div style={{ 
        backgroundColor: 'white', 
        borderBottom: '1px solid #e5e7eb',
        padding: '1rem 2rem'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 
            onClick={() => navigate('/')}
            style={{ 
              fontSize: '1.5rem', 
              fontWeight: '700', 
              color: '#10b981',
              cursor: 'pointer'
            }}
          >
            ReturnPal
          </h1>
          <Button 
            onClick={() => navigate('/')}
            variant="outline"
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <ArrowLeft size={16} />
            Back to Home
          </Button>
        </div>
      </div>

      {/* Login Form */}
      <div style={{ maxWidth: '500px', margin: '0 auto', padding: '4rem 1rem' }}>
        <Card>
          <CardHeader style={{ textAlign: 'center' }}>
            <LogIn size={48} color="#10b981" style={{ margin: '0 auto 1rem' }} />
            <CardTitle style={{ fontSize: '1.75rem', marginBottom: '0.5rem' }}>
              Customer Login
            </CardTitle>
            <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              Sign in to view your bookings and manage returns
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {/* Error Message */}
                {error && (
                  <div style={{ 
                    padding: '0.75rem', 
                    backgroundColor: '#fee2e2', 
                    border: '1px solid #fecaca',
                    borderRadius: '8px',
                    color: '#991b1b',
                    fontSize: '0.875rem'
                  }}>
                    {error}
                  </div>
                )}

                {/* Email Field */}
                <div>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    fontSize: '0.875rem', 
                    fontWeight: '600', 
                    marginBottom: '0.5rem',
                    color: '#374151'
                  }}>
                    <Mail size={16} style={{ marginRight: '0.5rem' }} />
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                    placeholder="john@example.com"
                    required
                  />
                </div>

                {/* Password Field */}
                <div>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    fontSize: '0.875rem', 
                    fontWeight: '600', 
                    marginBottom: '0.5rem',
                    color: '#374151'
                  }}>
                    <Lock size={16} style={{ marginRight: '0.5rem' }} />
                    Password
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                    placeholder="Enter your password"
                    required
                  />
                </div>

                {/* Login Button */}
                <Button 
                  type="submit"
                  disabled={isLoading}
                  style={{ 
                    padding: '1rem', 
                    backgroundColor: isLoading ? '#9ca3af' : '#10b981',
                    color: 'white',
                    fontSize: '1rem',
                    fontWeight: '600',
                    width: '100%'
                  }}
                >
                  {isLoading ? (
                    'Signing in...'
                  ) : (
                    <>
                      <LogIn size={18} style={{ marginRight: '0.5rem' }} />
                      Sign In
                    </>
                  )}
                </Button>
              </div>
            </form>

            {/* Additional Links */}
            <div style={{ 
              marginTop: '1.5rem', 
              paddingTop: '1.5rem', 
              borderTop: '1px solid #e5e7eb',
              textAlign: 'center'
            }}>
              <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                Don't have an account?{' '}
                <span 
                  onClick={() => navigate('/signup')}
                  style={{ color: '#10b981', fontWeight: '600', cursor: 'pointer' }}
                >
                  Sign Up
                </span>
              </p>
              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
                Need help?{' '}
                <span 
                  onClick={() => navigate('/contact')}
                  style={{ color: '#10b981', fontWeight: '600', cursor: 'pointer' }}
                >
                  Contact Support
                </span>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CustomerLoginPage;
